import React, { useState, useEffect } from 'react';
import { Box, Header, Icon } from '@cloudscape-design/components';
import { Link, useParams } from 'react-router-dom';
import { Role } from 'types/organizationTypes';
import { Member } from 'types/Members';
import { organizationService } from 'services/organizationService';
import RoleSelector from 'components/organizations/RoleSelector';
import { useNotification } from 'contexts/NotificationContext';
import { useBreadcrumbs } from 'contexts/BreadcrumbContext';
import AppConfig from 'configs/AppConfig';

const EditRoles: React.FC = () => {
    const { addNotification } = useNotification();
    const { setBreadcrumbs } = useBreadcrumbs();
    const { id, memberId } = useParams<{ id: string; memberId: string }>();
    const [roles, setRoles] = useState<Role[]>([]);
    const [selectedRoles, setSelectedRoles] = useState<Record<string, boolean>>({});
    const [loadingRoles, setLoadingRoles] = useState(true);
    const [member, setMember] = useState<Member | null>(null);
    const [organization, setOrganization] = useState<{ display_name: string } | null>(null);

    const fetchRoles = async () => {
        try {
            setLoadingRoles(true);
            const [fetchedRoles, membersResponse, orgData] = await Promise.all([
                organizationService.getRealmRoles(),
                organizationService.getOrganizationMembers(id!),
                organizationService.getOrganizationById(id!)
            ]);
            const member = membersResponse.members.find(m => m.id === memberId);

            if (!member) {
                throw new Error('Member not found');
            }

            setMember(member);
            setOrganization(orgData);
            const memberRoles = member.roles;

            setRoles(fetchedRoles);
            const initialSelectedRoles = fetchedRoles.reduce((acc, role) => ({
                ...acc,
                [role.name]: memberRoles.some((r: { name: string; }) => r.name === role.name)
            }), {});
            setSelectedRoles(initialSelectedRoles);
        } catch (error) {

        } finally {
            setLoadingRoles(false);
        }
    };

    useEffect(() => {
        fetchRoles();
    }, [id, memberId]);

    useEffect(() => {
        if (organization && member) {
            setBreadcrumbs([
                { text: AppConfig.APP_NAME, href: '/' },
                { text: 'Organizations', href: '/organizations' },
                { text: organization.display_name, href: `/organizations/${id}` },
                { text: `Edit Roles - ${member.username}`, href: `/organizations/${id}/members/${memberId}/edit-roles` }
            ]);
        }

        return () => {
            setBreadcrumbs([]);
        };
    }, [id, memberId, organization, member, setBreadcrumbs]);

    const toggleRole = async (roleName: string): Promise<void> => {
        if (!id || !memberId) return;

        const isSelected = selectedRoles[roleName];
        try {
            if (isSelected) {
                // Remove role
                await organizationService.removeRoleFromMember(id, memberId, roleName);
            } else {
                // Assign role
                await organizationService.editMemberRoles(id, memberId, roleName);
            }

            setSelectedRoles(prev => ({
                ...prev,
                [roleName]: !isSelected
            }));

            addNotification('success', `Role ${isSelected ? 'removed' : 'assigned'} successfully!`);
        } catch (error) {

        }
    };


    const selectAll = async (type: 'all' | 'none' | 'all manage' | 'all view' | 'all application'): Promise<void> => {
        if (!id || !memberId) return;

        try {
            const rolesToUpdate = roles.filter(role => {
                switch (type) {
                    case 'all manage':
                        return role.name.startsWith('manage-');
                    case 'all view':
                        return role.name.startsWith('view-');
                    case 'all application':
                        return role.name.startsWith('manage-clusters');
                    case 'all':
                        return true;
                    case 'none':
                        return false;
                    default:
                        return false;
                }
            });
            const rolesToRemove = roles.filter(role => !rolesToUpdate.includes(role));
            // For each role, either assign or remove based on type
            await Promise.all(rolesToUpdate.map(async (role) => {
                if (type === 'none') {
                    if (selectedRoles[role.name]) {
                        await organizationService.removeRoleFromMember(id, memberId, role.name);
                    }
                } else {
                    if (!selectedRoles[role.name]) {
                        await organizationService.editMemberRoles(id, memberId, role.name);
                    }
                }
            }));

            await Promise.all(rolesToRemove.map(async (role) => {
                if (selectedRoles[role.name]) {
                    await organizationService.removeRoleFromMember(id, memberId, role.name);
                }
            }));

            // Update local state after all API calls are complete
            const newRoles = roles.reduce((acc, role) => ({
                ...acc,
                [role.name]: type === 'all' ||
                    (type === 'all manage' && role.name.startsWith('manage-')) ||
                    (type === 'all view' && role.name.startsWith('view-')) ||
                    (type === 'all application' && role.name.startsWith('manage-clusters'))
            }), {});

            setSelectedRoles(newRoles);
            addNotification('success', `Roles updated successfully!`);
        } catch (error) {
            addNotification('error', `Failed to update roles`);
        }
    };


    return (
        <div className='edit-roles-page'>
            <Box margin={{ bottom: "l" }}>
                <div className="header-container">
                    <Icon name="user-profile" size="medium" />
                    <Link to={`/organizations/${id}`}>
                        <button className="organization-button">
                            Back
                        </button>
                    </Link>
                </div>
            </Box>

            <Box margin={{ bottom: "l" }}>
                <Header variant="h3">Edit roles of {member?.username || 'Loading...'} </Header>
            </Box>

            <div className="role-selector-wrapper">

                <RoleSelector
                    roles={roles}
                    selectedRoles={selectedRoles}
                    loadingRoles={loadingRoles}
                    toggleRole={toggleRole}
                    selectAll={selectAll}
                    defaultRoles={roles.filter(role => role.description === 'default').map(role => role.name)}

                />

            </div>
        </div>
    );
};

export default EditRoles;
